/**
 * Supabase Client
 *
 * Client-side Supabase instance for auth operations.
 * Uses the anon key for initial connection, then manages
 * session tokens from the Zylo auth flow.
 *
 * ## Authentication Flow
 *
 * 1. User submits credentials (email/password) via login/signup form
 * 2. Frontend calls Backend API (/auth/signin or /auth/signup)
 * 3. Backend creates HMAC-signed request, calls Supabase Edge Function
 * 4. Edge Function validates signature, authenticates user with Supabase Auth
 * 5. Edge Function returns JWT with projectId in app_metadata.project_id
 * 6. Backend returns session (accessToken, refreshToken, user) to frontend
 * 7. Frontend validates projectId matches NEXT_PUBLIC_PROJECT_ID
 * 8. Frontend calls setSupabaseSession() to store tokens in Supabase client
 * 9. Supabase client persists session to localStorage and enables auto-refresh
 * 10. onAuthStateChange fires, updating React auth context
 *
 * ## Session Management
 *
 * Once the session is set via setSupabaseSession():
 * - Supabase client automatically refreshes tokens before expiry
 * - Session persists across page reloads (localStorage)
 * - onAuthStateChange notifies React context of auth state changes
 * - API requests use getAccessToken() which returns fresh tokens
 *
 * ## Multi-Tenant Security
 *
 * The projectId in the JWT's app_metadata is validated against
 * NEXT_PUBLIC_PROJECT_ID to ensure users from one project cannot
 * access another project's frontend. This is critical for multi-tenant
 * deployments sharing the same Supabase instance.
 *
 * ## Required Environment Variables
 *
 * - NEXT_PUBLIC_SUPABASE_URL: Supabase project URL
 * - NEXT_PUBLIC_SUPABASE_ANON_KEY: Supabase anon key (JWT format)
 * - NEXT_PUBLIC_PROJECT_ID: Expected project ID for validation
 * - NEXT_PUBLIC_BACKEND_URL: Backend API URL for auth endpoints
 */

import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js';

// =============================================================================
// Configuration
// =============================================================================

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// Debug: Log config on load
console.log('[Supabase] Initializing with URL:', SUPABASE_URL ? SUPABASE_URL.substring(0, 30) + '...' : 'NOT SET');
console.log('[Supabase] Anon key:', SUPABASE_ANON_KEY ? 'SET (' + SUPABASE_ANON_KEY.substring(0, 20) + '...)' : 'NOT SET');

if (!SUPABASE_URL) {
  console.error('[Supabase] NEXT_PUBLIC_SUPABASE_URL is not set - auth will not work!');
}

if (!SUPABASE_ANON_KEY) {
  console.error('[Supabase] NEXT_PUBLIC_SUPABASE_ANON_KEY is not set - auth will not work!');
}

// =============================================================================
// Supabase Client
// =============================================================================

export const supabase: SupabaseClient = createClient(
  SUPABASE_URL || 'https://placeholder.supabase.co',
  SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NDUxOTI4MDAsImV4cCI6MTk2MDc2ODgwMH0.placeholder',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
  }
);

// =============================================================================
// Session Management
// =============================================================================

/**
 * Set the Supabase session from Zylo auth tokens.
 * Call this after successful signIn/signUp via Zylo backend.
 */
export async function setSupabaseSession(
  accessToken: string,
  refreshToken: string
): Promise<{ user: User | null; error: Error | null }> {
  console.log('[Supabase] setSupabaseSession called');
  console.log('[Supabase] Access token (first 50 chars):', accessToken?.substring(0, 50) + '...');
  console.log('[Supabase] Refresh token:', refreshToken);

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.error('[Supabase] Cannot set session - Supabase not configured!');
    return { user: null, error: new Error('Supabase not configured') };
  }

  try {
    console.log('[Supabase] Calling supabase.auth.setSession...');
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      console.error('[Supabase] setSession returned error:', error.message, error);
      return { user: null, error };
    }

    console.log('[Supabase] Session set successfully!');
    console.log('[Supabase] User:', data.user?.email);
    console.log('[Supabase] Session exists:', !!data.session);

    // Verify session was actually stored
    const { data: verifyData } = await supabase.auth.getSession();
    console.log('[Supabase] Verify - Session in storage:', !!verifyData.session);

    return { user: data.user, error: null };
  } catch (err) {
    console.error('[Supabase] Error setting session:', err);
    return { user: null, error: err instanceof Error ? err : new Error('Unknown error') };
  }
}

/**
 * Get the current Supabase session.
 */
export async function getSupabaseSession(): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get a fresh access token.
 * Supabase will automatically refresh if needed.
 */
export async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

/**
 * Clear the Supabase session.
 */
export async function clearSupabaseSession(): Promise<void> {
  await supabase.auth.signOut();
  console.log('[Supabase] Session cleared');
}

/**
 * Subscribe to auth state changes.
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
): { unsubscribe: () => void } {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });

  return { unsubscribe: () => subscription.unsubscribe() };
}

// =============================================================================
// ProjectId Validation
// =============================================================================

/**
 * Decode JWT payload without verification.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = atob(base64);
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

/**
 * Extract projectId from JWT access token.
 */
export function getProjectIdFromToken(accessToken: string): string | null {
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return null;

  const appMetadata = payload.app_metadata as Record<string, unknown> | undefined;
  if (appMetadata?.project_id && typeof appMetadata.project_id === 'string') {
    return appMetadata.project_id;
  }

  return null;
}

/**
 * Validate that the token's projectId matches the expected projectId.
 */
export function validateProjectId(accessToken: string): boolean {
  const expectedProjectId = process.env.NEXT_PUBLIC_PROJECT_ID;

  // Skip validation in dev mode
  if (!expectedProjectId || expectedProjectId === 'dev') {
    return true;
  }

  const tokenProjectId = getProjectIdFromToken(accessToken);
  if (!tokenProjectId) {
    console.warn('[Supabase] Could not extract projectId from JWT');
    return true; // Allow if we can't validate (backwards compatibility)
  }

  if (tokenProjectId !== expectedProjectId) {
    console.warn(
      `[Supabase] ProjectId mismatch. Expected: ${expectedProjectId}, Got: ${tokenProjectId}`
    );
    return false;
  }

  return true;
}
